import { supabase } from './supabase';
import { createOption, addOptionValue } from './menuEditor';
import { BOISSON_MENU_SLUG, MENU_DRINK_TRIGGER } from './menuDrinks';

/**
 * Bibliothèque d'options PAR DÉFAUT (seed à la création d'un resto).
 * Source : patterns réels Uber Eats (Uber_Options_Library.json — Food Kourt /
 * Gratine Moi / Nomad'z). Objectif : le restaurateur démarre avec une bibliothèque
 * riche (sauces, crudités, viandes, suppléments, boissons, desserts, format) et
 * AFFECTE ensuite librement ces groupes à ses produits dans le back-office.
 *
 * Important (apprentissages legacy v17-v20) : on NE force RIEN sur les produits —
 * on remplit seulement la bibliothèque (`options` + `option_values`). Aucun lien
 * `product_options` créé : le client garde la main (mode strict admin).
 *
 * Les `slug` reprennent CLIENT_OPTION_ORDER (productOptions.ts) pour un affichage
 * ordonné côté client : format → viande → crudités → sauces → suppléments →
 * boisson extra → dessert.
 */

export interface SeedOptionValue {
  name: string;
  priceDeltaCents?: number;
  isDefault?: boolean;
}

export interface SeedOption {
  slug: string;
  name: string;
  selection: 'single' | 'multiple';
  isRequired?: boolean;
  minChoices?: number;
  /** `null` = illimité (groupes multiples) ; absent = 1 pour single. */
  maxChoices?: number | null;
  pricingMode?: 'flat' | 'first_free';
  extraEachCents?: number;
  /** Option « boisson au menu » : valeurs auto (catégorie Boissons), déclenchée par « boisson ». */
  isMenuDrink?: boolean;
  values: SeedOptionValue[];
}

export const DEFAULT_OPTIONS_LIBRARY: SeedOption[] = [
  {
    slug: 'format',
    name: 'Format',
    selection: 'single',
    isRequired: false,
    maxChoices: 1,
    values: [
      { name: 'Seul', priceDeltaCents: 0, isDefault: true },
      { name: 'En menu (frites + boisson)', priceDeltaCents: 200 },
    ],
  },
  {
    slug: 'taille',
    name: 'Taille',
    selection: 'single',
    isRequired: false,
    maxChoices: 1,
    values: [
      { name: 'M · 1 viande', priceDeltaCents: 0, isDefault: true },
      { name: 'L · 2 viandes', priceDeltaCents: 100 },
      { name: 'XL · 3 viandes', priceDeltaCents: 200 },
    ],
  },
  {
    slug: 'pain_galette',
    name: 'Type de pain',
    selection: 'single',
    isRequired: false,
    maxChoices: 1,
    values: [
      { name: 'Pain', priceDeltaCents: 0, isDefault: true },
      { name: 'Galette', priceDeltaCents: 50 },
    ],
  },
  {
    slug: 'viande_tacos',
    name: 'Viande au choix',
    selection: 'single',
    isRequired: false,
    maxChoices: 1,
    values: [
      { name: 'Kefta' },
      { name: 'Kebab' },
      { name: 'Steak' },
      { name: 'Escalope' },
      { name: 'Chicken' },
      { name: 'Cordon bleu' },
      { name: 'Tenders' },
      { name: 'Nuggets' },
      { name: 'Merguez' },
    ],
  },
  {
    slug: 'supplement_viande',
    name: 'Supplément viande',
    selection: 'multiple',
    minChoices: 0,
    maxChoices: null,
    values: [
      { name: 'Kebab', priceDeltaCents: 200 },
      { name: 'Steak', priceDeltaCents: 200 },
      { name: 'Chicken', priceDeltaCents: 200 },
      { name: 'Cordon bleu', priceDeltaCents: 200 },
      { name: 'Tenders', priceDeltaCents: 200 },
      { name: 'Nuggets', priceDeltaCents: 200 },
      { name: 'Merguez', priceDeltaCents: 200 },
    ],
  },
  {
    slug: 'crudites',
    name: 'Crudités',
    selection: 'multiple',
    minChoices: 0,
    maxChoices: null,
    values: [
      { name: 'Salade' },
      { name: 'Tomate' },
      { name: 'Oignons' },
      { name: 'Cornichons', priceDeltaCents: 50 },
      { name: 'Olives', priceDeltaCents: 70 },
      { name: 'Maïs', priceDeltaCents: 50 },
    ],
  },
  {
    slug: 'sauces',
    name: 'Sauces (3 max)',
    selection: 'multiple',
    minChoices: 0,
    maxChoices: 3,
    values: [
      { name: 'Harissa' },
      { name: 'Ketchup' },
      { name: 'Blanche' },
      { name: 'Andalouse' },
      { name: 'Samouraï' },
      { name: 'Poivre' },
      { name: 'Biggy Burger' },
      { name: 'Américaine' },
      { name: 'Barbecue' },
      { name: 'Algérienne' },
      { name: 'Mayonnaise' },
      { name: 'Moutarde' },
    ],
  },
  {
    slug: 'supplements',
    name: 'Suppléments',
    selection: 'multiple',
    minChoices: 0,
    maxChoices: null,
    values: [
      { name: 'Cheddar', priceDeltaCents: 50 },
      { name: 'Boursin', priceDeltaCents: 100 },
      { name: 'Emmental', priceDeltaCents: 100 },
      { name: 'Chèvre', priceDeltaCents: 100 },
      { name: 'Œuf', priceDeltaCents: 100 },
      { name: 'Piment', priceDeltaCents: 100 },
      { name: 'Poivrons', priceDeltaCents: 100 },
      { name: 'Olives', priceDeltaCents: 100 },
      { name: 'Bacon', priceDeltaCents: 120 },
      { name: 'Dinde', priceDeltaCents: 120 },
    ],
  },
  {
    slug: BOISSON_MENU_SLUG,
    name: 'Boisson au menu',
    selection: 'single',
    isRequired: true,
    maxChoices: 1,
    isMenuDrink: true, // valeurs auto depuis la catégorie Boissons ; s'affiche si format « …boisson… »
    values: [],
  },
  {
    slug: 'boissons_extra',
    name: 'Une boisson en plus ?',
    selection: 'single',
    maxChoices: 1,
    values: [
      { name: 'Coca-Cola', priceDeltaCents: 250 },
      { name: 'Coca-Cola zéro', priceDeltaCents: 250 },
      { name: 'Fanta', priceDeltaCents: 250 },
      { name: 'Oasis', priceDeltaCents: 250 },
      { name: 'Ice Tea', priceDeltaCents: 250 },
      { name: 'Perrier', priceDeltaCents: 250 },
      { name: 'Eau 50 cl', priceDeltaCents: 180 },
    ],
  },
  {
    slug: 'dessert_suggestion',
    name: 'Envie d’un dessert ?',
    selection: 'single',
    maxChoices: 1,
    values: [
      { name: 'Pas de dessert', priceDeltaCents: 0, isDefault: true },
      { name: 'Tiramisu caramel', priceDeltaCents: 495 },
      { name: 'Tiramisu Daim', priceDeltaCents: 495 },
      { name: 'Tiramisu Oreo', priceDeltaCents: 495 },
      { name: 'Donuts', priceDeltaCents: 390 },
    ],
  },
];

/** Nombre total de valeurs dans la bibliothèque (pratique pour logs/tests). */
export function defaultOptionsValueCount(): number {
  return DEFAULT_OPTIONS_LIBRARY.reduce((n, o) => n + o.values.length, 0);
}

/**
 * Seed la bibliothèque d'options par défaut pour un tenant — mode « TOP-UP ».
 * N'ajoute que les groupes dont le `slug` n'existe PAS déjà : non destructif
 * (on ne touche jamais aux options que le resto a déjà créées/personnalisées).
 * → Sur un resto neuf : pose toute la bibliothèque. Sur un resto existant :
 *   complète avec les nouveaux groupes (ex. après enrichissement de la lib).
 * Best-effort : tolérant aux erreurs partielles (renvoie le nombre de groupes créés).
 */
export async function seedDefaultOptions(tenantId: string): Promise<{ created: number }> {
  const { data: existing } = await supabase
    .from('options')
    .select('slug')
    .eq('tenant_id', tenantId);
  const have = new Set(((existing as { slug: string }[] | null) ?? []).map((o) => o.slug));

  let created = 0;
  let position = have.size;
  for (const o of DEFAULT_OPTIONS_LIBRARY) {
    if (have.has(o.slug)) continue; // déjà présent → respecté (top-up non destructif)
    const opt = await createOption(tenantId, {
      name: o.name,
      slug: o.slug,
      selection: o.selection,
      is_required: o.isRequired ?? false,
      min_choices: o.minChoices ?? 0,
      max_choices: o.maxChoices === undefined ? (o.selection === 'single' ? 1 : null) : o.maxChoices,
      position: position++,
      pricing_mode: o.pricingMode ?? 'flat',
      extra_each_cents: o.extraEachCents ?? 0,
      // Boisson au menu : marqueur de dépendance (« s'affiche si un format contient boisson »).
      // Les valeurs sont injectées automatiquement au chargement (catégorie Boissons).
      ...(o.isMenuDrink ? { depends_on_option_slug: null, depends_on_value: MENU_DRINK_TRIGGER } : {}),
    });
    if (!opt) continue;
    created++;
    // Boisson au menu : aucune valeur saisie (auto depuis la catégorie Boissons).
    if (o.isMenuDrink) continue;
    for (let j = 0; j < o.values.length; j++) {
      const v = o.values[j];
      await addOptionValue(opt.id, {
        name: v.name,
        price_delta_cents: v.priceDeltaCents ?? 0,
        is_default: v.isDefault ?? false,
        is_available: true,
        position: j,
      });
    }
  }
  return { created };
}
